import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { APP_CONFIG, formatDate as formatDateGlobal } from '../../../config/appConfig';
import { createGetTableActions, getAccountingToolbarIcon } from '../shared/tableActions';
import { formatCurrencyCodeSymbol } from './currencyDisplay';
import type { AccCurrencyRate } from './useData';

const formatRate = (value: number | string) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 });
};

const toPayload = (data: Record<string, unknown>) => ({
  currencyId: Number(data.currencyId),
  rateDate: data.rateDate,
  rate: Number(data.rate)});

export const moduleConfig = {
  module: {
    title: 'Currency rates',
    permissionModuleKey: 'acc_currency_rates',
    route: {
      list: '/accounting/currency-rates',
      new: '/accounting/currency-rates/new',
      detail: (id: number) => `/accounting/currency-rates/${id}`,
      edit: (id: number) => `/accounting/currency-rates/${id}/edit`},
    api: { list: '/acc-currency-rates', detail: (id: number) => `/acc-currency-rates/${id}` }},
  pagination: { initialItemsPerPage: APP_CONFIG.table.defaultPageSize, itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions },
  search: { placeholder: 'Search by currency code…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: AccCurrencyRate[]) => items ,
  },
  toolbar: {
    actions: [{
      key: 'add',
      icon: 'add',
      title: 'New rate',
      showTitle: false,
      variant: 'primary' as const,
      href: '/accounting/currency-rates/new'}]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: AccCurrencyRate) => item.id,
  },
  emptyStates: {
    noData: 'Enter exchange rates for journal posting.',
    noSearchResults: 'No matches.',
    noFilterResults: 'No matches.',
  },
  delete: {
    title: 'Delete currency rate',
    message: 'Delete this rate?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deleteAccCurrencyRate } = await import('../../../services/mutations');
      await deleteAccCurrencyRate(id);
    }},
  utils: { formatDate: (d?: string) => (d ? formatDateGlobal(d) : '') },
  detail: {
    subtitle: 'Currency exchange rate',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Rate not found.',
    notFoundBackText: 'Back',
    defaultTitle: 'Currency rate',
    getTitle: (item: AccCurrencyRate) =>
      `${item.currency?.code ?? item.currencyId} — ${formatDateGlobal(item.rateDate?.slice(0, 10))}`},
  form: {
    new: { title: 'New currency rate', subtitle: 'Enter USD exchange rate for a currency on a given date', submitText: 'Create' },
    edit: { title: 'Edit currency rate', subtitle: 'Update exchange rate', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formSizing: 'content' as const,
    formMaxWidth: 'max-w-3xl',
    create: async (data: Partial<AccCurrencyRate>) => {
      const { createAccCurrencyRate } = await import('../../../services/mutations');
      return createAccCurrencyRate(toPayload(data as Record<string, unknown>));
    },
    update: async (id: number, data: Partial<AccCurrencyRate>) => {
      const { updateAccCurrencyRate } = await import('../../../services/mutations');
      return updateAccCurrencyRate(id, toPayload(data as Record<string, unknown>));
    }},
  fields: [
    {
      name: 'currencyId',
      label: 'Currency',
      type: 'select',
      required: true,
      placeholder: 'Select currency',
      apiEndpoint: '/currencies',
      valueField: 'id',
      mapApiItemToSelectOption: (item: { id: number; code: string; name: string; symbol?: string | null }) => ({
        value: item.id,
        label: item.name || formatCurrencyCodeSymbol(item)}),
      options: [{ value: '', label: 'Select currency' }],
      validation: { required: true }},
    {
      name: 'rateDate',
      label: 'Rate date',
      type: 'date',
      required: true,
      validation: { required: true }},
    {
      name: 'rate',
      label: 'Rate',
      type: 'number',
      required: true,
      validation: { required: true, min: 0 },
      helperText: 'USD per 1 unit of the selected currency (same convention as journal lines).'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: 'Exchange rate',
        description: 'One rate per currency and date.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['currencyId', 'rateDate', 'rate'],
        fieldSpans: {
          currencyId: 'md:col-span-6',
          rateDate: 'md:col-span-3',
          rate: 'md:col-span-3'},
        collapsible: false},
    ]} as FormLayout};

export const getColumns = (
  formatDate: (d?: string) => string,
  options?: { hideCurrency?: boolean },
): Column<AccCurrencyRate>[] => {
  const cols: Column<AccCurrencyRate>[] = [
  {
    key: 'rateDate',
    header: 'Rate date',
    sortable: true,
    render: (v) => formatDate(typeof v === 'string' ? v.slice(0, 10) : undefined)},
  {
    key: 'currency',
    header: 'Currency',
    sortable: true,
    render: (_v, row) => formatCurrencyCodeSymbol(row.currency, row.currencyId)},
  {
    key: 'rate',
    header: 'Rate (USD)',
    sortable: true,
    align: 'right',
    render: (v) => formatRate(v)},
];
  return options?.hideCurrency ? cols.filter((c) => c.key !== 'currency') : cols;
};

export const getDetailSections = (item: AccCurrencyRate, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Currency', value: formatCurrencyCodeSymbol(item.currency, item.currencyId) },
      { label: 'Rate date', value: formatDate(item.rateDate?.slice(0, 10)) },
      { label: 'Rate (USD)', value: formatRate(item.rate) },
    ]},
];

export const getTableActions = createGetTableActions<AccCurrencyRate>(moduleConfig.module.route);

export const getToolbarIcon = getAccountingToolbarIcon;

export const getInitialFormData = (): Partial<AccCurrencyRate> => ({
  currencyId: '' as unknown as number,
  rateDate: new Date().toISOString().slice(0, 10),
  rate: '' as unknown as number});

export const mapItemToFormData = (item: AccCurrencyRate): Partial<AccCurrencyRate> => ({
  currencyId: String(item.currencyId) as unknown as number,
  rateDate: item.rateDate?.slice(0, 10) ?? '',
  rate: item.rate != null ? String(item.rate) as unknown as number : ('' as unknown as number)});
